import { useLocation, useParams, Navigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { CheckCircle2, Download, Loader2, RefreshCw, Magnet } from "lucide-react";
import { useState, useEffect } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { usePixelTracking } from "@/hooks/usePixelTracking";

interface FormData {
  id: string;
  title: string;
  slug: string;
  description: string | null;
  logo_url: string | null;
  color_primary: string | null;
  color_secondary: string | null;
  color_accent: string | null;
  thank_you_message: string | null;
  ga_pixel_id: string | null;
  meta_pixel_id: string | null;
  ai_final_prompt?: string | null;
  vsl_youtube_url?: string | null;
}

interface LocationState {
  form: FormData;
  pdfHtml: string | null;
  pdfUrl?: string | null;
  rewardFileUrl?: string | null;
  responses: Array<{ question: string; answer: string }>;
  sessionId?: string;
  leadEmail?: string;
}

export default function FormResult() {
  const { slug } = useParams<{ slug: string }>();
  const location = useLocation();
  const state = location.state as LocationState | null;
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [pdfContent, setPdfContent] = useState<string | null>(null);
  const [loadingPdf, setLoadingPdf] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);

  // If no state, redirect to form
  if (!state?.form) {
    return <Navigate to={`/f/${slug}`} replace />;
  }

  const { form, pdfHtml, pdfUrl, rewardFileUrl, sessionId, responses, leadEmail } = state;
  const primaryColor = form.color_primary || "#6366f1";
  const accentColor = form.color_accent || "#10b981";
  const isFileReward = !!rewardFileUrl;

  // Extract YouTube video ID from URL
  const getYoutubeEmbedUrl = (url: string | null | undefined): string | null => {
    if (!url) return null;
    
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    
    if (match && match[2].length === 11) {
      return `https://www.youtube.com/embed/${match[2]}?rel=0`;
    }
    return null;
  };

  const youtubeEmbedUrl = getYoutubeEmbedUrl(form.vsl_youtube_url);

  // Fetch PDF from storage if we have URL but no inline HTML (skip for file rewards)
  useEffect(() => {
    if (isFileReward) {
      // For file rewards, no need to fetch PDF content
      return;
    }

    const fetchPdfFromStorage = async () => {
      if (pdfHtml) {
        setPdfContent(pdfHtml);
        return;
      }

      if (pdfUrl) {
        setLoadingPdf(true);
        try {
          const response = await fetch(pdfUrl);
          if (response.ok) {
            const html = await response.text();
            setPdfContent(html);
          }
        } catch (error) {
          console.error("Error fetching PDF from storage:", error);
        } finally {
          setLoadingPdf(false);
        }
        return;
      }

      // If no pdfHtml and no pdfUrl, try to get from session
      if (sessionId) {
        setLoadingPdf(true);
        try {
          const { data: session } = await supabase
            .from("form_sessions")
            .select("pdf_url")
            .eq("id", sessionId)
            .single();

          if (session?.pdf_url) {
            const response = await fetch(session.pdf_url);
            if (response.ok) {
              const html = await response.text();
              setPdfContent(html);
            }
          }
        } catch (error) {
          console.error("Error fetching PDF URL from session:", error);
        } finally {
          setLoadingPdf(false);
        }
      }
    };

    fetchPdfFromStorage();
  }, [pdfHtml, pdfUrl, sessionId]);

  // Pixel Tracking
  const { trackPdfDownload } = usePixelTracking({
    gaPixelId: form.ga_pixel_id,
    metaPixelId: form.meta_pixel_id,
  });

  // Generate PDF retry function
  const handleGeneratePdf = async () => {
    if (!sessionId || !responses) return;

    setGeneratingPdf(true);

    try {
      const { data, error } = await supabase.functions.invoke("generate-pdf", {
        body: {
          formConfig: {
            title: form.title,
            description: form.description,
            aiFinalPrompt: form.ai_final_prompt,
            logoUrl: form.logo_url,
            colorPrimary: form.color_primary || "#6366f1",
            colorSecondary: form.color_secondary || "#f1f5f9",
            colorAccent: form.color_accent || "#10b981",
          },
          responses: responses.map((r) => ({
            question: r.question,
            answer: r.answer,
          })),
          leadEmail: leadEmail || "unknown@lead.com",
          sessionId,
        },
      });

      if (error) {
        console.error("Error generating PDF:", error);
        toast.error("Erro ao gerar documento. Tente novamente.");
        return;
      }

      if (data?.fullHtml) {
        setPdfContent(data.fullHtml);
        toast.success("Documento gerado com sucesso!");
      } else {
        toast.error("Erro ao gerar documento. Tente novamente.");
      }
    } catch (error) {
      console.error("Error calling generate-pdf:", error);
      toast.error("Erro ao gerar documento. Tente novamente.");
    } finally {
      setGeneratingPdf(false);
    }
  };

  const handleDownloadPdf = async () => {
    setDownloadingPdf(true);

    try {
      // Track download event (GA/Meta pixels)
      trackPdfDownload();

      // Track the download in the database as a special response
      if (sessionId) {
        const { error: responseError } = await supabase
          .from("form_responses")
          .insert({
            session_id: sessionId,
            question: "__PDF_DOWNLOAD__",
            answer: new Date().toISOString(),
            question_order: 9999,
            is_ai_generated: false,
          });
        
        if (responseError) {
          console.error("Error tracking PDF download:", responseError);
        }
      }

      if (isFileReward && rewardFileUrl) {
        // For file rewards, open the file URL directly
        window.open(rewardFileUrl, "_blank");
        toast.success("Download iniciado!");
      } else if (pdfContent) {
        // For AI-generated PDFs, open in new window for printing
        const printWindow = window.open("", "_blank");
        if (printWindow) {
          printWindow.document.write(pdfContent);
          printWindow.document.close();
          setTimeout(() => {
            printWindow.print();
          }, 500);
        } else {
          // Fallback: download as HTML file
          const blob = new Blob([pdfContent], { type: "text/html" });
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = `${form.title.replace(/\s+/g, "-").toLowerCase()}-resultado.html`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
          toast.success("Arquivo baixado!");
        }
      }
    } catch (error) {
      console.error("Error downloading:", error);
      toast.error("Erro ao baixar");
    } finally {
      setDownloadingPdf(false);
    }
  };

  // Check if content is ready (either file reward URL or AI PDF content)
  const contentReady = isFileReward ? !!rewardFileUrl : !!pdfContent;

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4"
      style={{
        background: `linear-gradient(135deg, ${primaryColor}15, hsl(240 20% 98%))`,
      }}
    >
      <Card className="w-full max-w-lg text-center overflow-hidden">
        {/* Decorative header */}
        <div 
          className="h-2 w-full"
          style={{ background: `linear-gradient(90deg, ${primaryColor}, ${accentColor})` }}
        />
        
        <CardContent className="py-12 px-8 space-y-6">
          {/* Success icon */}
          <div className="relative mx-auto w-fit">
            <div
              className="w-20 h-20 rounded-full flex items-center justify-center animate-pulse-slow"
              style={{ backgroundColor: `${accentColor}20` }}
            >
              <div
                className="w-16 h-16 rounded-full flex items-center justify-center"
                style={{ backgroundColor: accentColor }}
              >
                <CheckCircle2 className="h-8 w-8 text-white" />
              </div>
            </div>
            <div 
              className="absolute -top-1 -right-1 w-6 h-6 rounded-full flex items-center justify-center"
              style={{ backgroundColor: primaryColor }}
            >
              <Magnet className="h-3 w-3 text-white" />
            </div>
          </div>

          {/* Logo */}
          {form.logo_url && (
            <img
              src={form.logo_url}
              alt="Logo"
              className="h-12 w-auto mx-auto object-contain"
            />
          )}

          {/* Messages */}
          <div className="space-y-2">
            <h1 className="text-2xl font-display font-bold">Obrigado! 🎉</h1>
            <p className="text-muted-foreground">
              {form.thank_you_message || "Suas respostas foram enviadas com sucesso."}
            </p>
          </div>

          {/* YouTube VSL Video */}
          {youtubeEmbedUrl && (
            <div className="pt-4 w-full">
              <div className="relative w-full rounded-xl overflow-hidden shadow-lg" style={{ paddingBottom: '56.25%' }}>
                <iframe
                  className="absolute top-0 left-0 w-full h-full"
                  src={youtubeEmbedUrl}
                  title="Vídeo de apresentação"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                />
              </div>
              <p className="text-xs text-muted-foreground text-center mt-2">
                👆 Assista o vídeo acima para saber como usar sua recompensa
              </p>
            </div>
          )}
          
          {/* PDF Section */}
          {!isFileReward && loadingPdf && (
            <div className="pt-6 space-y-4">
              <div 
                className="p-4 rounded-xl"
                style={{ backgroundColor: `${primaryColor}10` }}
              >
                <div className="flex items-center justify-center gap-2">
                  <Loader2 className="h-5 w-5 animate-spin" style={{ color: primaryColor }} />
                  <span className="font-medium">Carregando seu conteúdo...</span>
                </div>
              </div>
            </div>
          )}

          {/* Generate PDF button (when PDF failed to generate - AI rewards only) */}
          {!isFileReward && !loadingPdf && !pdfContent && sessionId && responses && (
            <div className="pt-6 space-y-4">
              <div 
                className="p-4 rounded-xl"
                style={{ backgroundColor: `${primaryColor}10` }}
              >
                <div className="flex items-center justify-center gap-2 mb-2">
                  <Magnet className="h-5 w-5" style={{ color: primaryColor }} />
                  <span className="font-medium">Seu conteúdo está quase pronto!</span>
                </div>
                <p className="text-sm text-muted-foreground">
                  Clique abaixo para gerar seu material personalizado.
                </p>
              </div>
              
              <Button
                onClick={handleGeneratePdf}
                disabled={generatingPdf}
                size="lg"
                className="w-full gap-2 text-white"
                style={{ backgroundColor: primaryColor }}
              >
                {generatingPdf ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    Gerando documento...
                  </>
                ) : (
                  <>
                    <RefreshCw className="h-5 w-5" />
                    Gerar meu documento
                  </>
                )}
              </Button>
            </div>
          )}

          {/* Download button (when content is ready) */}
          {contentReady && (
            <div className="pt-6 space-y-4">
              <div 
                className="p-4 rounded-xl"
                style={{ backgroundColor: `${primaryColor}10` }}
              >
                <div className="flex items-center justify-center gap-2 mb-2">
                  <Magnet className="h-5 w-5" style={{ color: primaryColor }} />
                  <span className="font-medium">Seu conteúdo está pronto!</span>
                </div>
                <p className="text-sm text-muted-foreground">
                  Criamos um material personalizado especialmente para você.
                </p>
              </div>
              
              <Button
                onClick={handleDownloadPdf}
                disabled={downloadingPdf}
                size="lg"
                className="w-full gap-2 text-white"
                style={{ backgroundColor: primaryColor }}
              >
                {downloadingPdf ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    Preparando...
                  </>
                ) : (
                  <>
                    <Download className="h-5 w-5" />
                    Baixar meu conteúdo
                  </>
                )}
              </Button>
            </div>
          )}

          {/* CTA Footer */}
          <div className="pt-6 border-t border-border/50">
            <a 
              href="https://magnetlink.lovable.app" 
              target="_blank" 
              rel="noopener noreferrer"
              className="group block p-4 rounded-xl border-2 border-dashed border-primary/30 hover:border-primary/60 hover:bg-primary/5 transition-all duration-300"
            >
              <div className="flex items-center justify-center gap-2 mb-1">
                <Magnet className="h-5 w-5 group-hover:scale-110 transition-transform" style={{ color: accentColor }} />
                <span className="font-semibold text-sm" style={{ color: primaryColor }}>
                  Quer criar seu próprio formulário?
                </span>
              </div>
              <p className="text-xs text-muted-foreground group-hover:text-foreground transition-colors">
                Acesse o <span className="font-medium" style={{ color: accentColor }}>Magnet Link</span> e crie formulários inteligentes grátis!
              </p>
            </a>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
